import React, { useState, useCallback, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import HeaderTitle from '_/components/header-title'
import { useParams, navigate } from '@reach/router'
import { getInspectionBudgetByIdSelector } from '_modules/inspectionBudget/selector'
import { SaveButton, CancelButton, DuplicateBudget, EditButton } from './Buttons'
import useStyles from './styles'
import { IconButton } from '@material-ui/core'
import Svg, { ICON } from '_components/svg'
import {
  handleDownloadPdf,
  getInspectionBudgetPdfExportMode,
  PDF_EXPORT_MODES,
} from '../../utils/functions'
import { getAgency } from '_/modules/agency/actions'
import { useToast } from '_/hooks/use-toast'
import { useInspectionLoading } from '../../hooks/use-inspection-loading'
import { formatDate } from '_utils/helpers'
import { VIEW_TITLE, LIST_ROUTES } from '../../utils/constants'
import { PdfExportModeModal } from '../modals/pdf-export-mode-modal'

const CreationDate = ({ value }) => {
  const styles = useStyles()

  return (
    value && (
      <span className={styles.createdDateContainer}>{`Criado em: ${formatDate(value)}`}</span>
    )
  )
}

export function Header() {
  const { budgetId } = useParams()
  const budget = useSelector(getInspectionBudgetByIdSelector)
  const { isEditing, budgetType } = useInspectionLoading()
  const styles = useStyles()
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const [loading, setLoading] = useState(false)
  const [isPdfModeModalOpen, setIsPdfModeModalOpen] = useState(false)
  const [defaultPdfMode, setDefaultPdfMode] = useState(PDF_EXPORT_MODES.IDEAL)
  const [showMaximumRecommendation, setShowMaximumRecommendation] = useState(false)
  const agencyExceptionCacheRef = useRef(new Map())

  const renderTitle = () => {
    const originalValue = budget?.isOriginal ? ' - Original' : ''
    const title = budgetId
      ? `${VIEW_TITLE[budgetType]} n.º ${budgetId}${originalValue}`
      : `Novo ${VIEW_TITLE[budgetType]}`

    return (
      <span className={styles.titleContainer}>
        <span>{title}</span>
        {budgetId && <CreationDate value={budget?.createdAt} />}
      </span>
    )
  }

  const handleBack = useCallback(() => {
    navigate(LIST_ROUTES[budgetType])
  }, [navigate, budgetType])

  const handleDownload = useCallback(
    async event => {
      event.preventDefault()
      event.stopPropagation()

      if (!budget?.id) return

      if (budgetType === 'insurance') {
        const agencyId = budget?.agency?.id
        let cityExceptionList = agencyExceptionCacheRef.current.get(agencyId)

        if (!cityExceptionList && agencyId) {
          try {
            const agencyData = await dispatch(getAgency(agencyId))
            cityExceptionList = agencyData?.insuranceCityExceptionList || []
            agencyExceptionCacheRef.current.set(agencyId, cityExceptionList)
          } catch (error) {
            cityExceptionList = []
          }
        }

        const defaultMode = getInspectionBudgetPdfExportMode({
          city: budget?.city,
          state: budget?.state,
          cityExceptionList,
        })
        setDefaultPdfMode(defaultMode)
        setShowMaximumRecommendation(defaultMode === PDF_EXPORT_MODES.MAXIMUM)
        setIsPdfModeModalOpen(true)
        return
      }

      await handleDownloadPdf({
        id: budget.id,
        dispatch,
        showToast,
        setLoading,
        pdfFileSuffix: budget?.pdfFileSuffix,
        pdfReferenceId: budget?.pdfReferenceId,
      })
    },
    [budget, budgetType, dispatch, showToast]
  )

  const handleConfirmPdfMode = useCallback(
    async selectedMode => {
      if (!budget?.id) return

      await handleDownloadPdf({
        id: budget.id,
        dispatch,
        showToast,
        setLoading,
        pdfFileSuffix: budget?.pdfFileSuffix,
        pdfReferenceId: budget?.pdfReferenceId,
        exportMode: selectedMode,
      })
      setIsPdfModeModalOpen(false)
    },
    [budget, budgetType, dispatch, showToast]
  )
  return (
    <HeaderTitle title={renderTitle()} backButtonAction={handleBack}>
      <div className={styles.headerButtonsContainer}>
        {!isEditing && (
          <>
            <IconButton onClick={handleDownload} disabled={!budgetId || loading}>
              <Svg type={ICON.DOWNLOAD} />
            </IconButton>
            <DuplicateBudget />
            <EditButton />
          </>
        )}
        {isEditing && (
          <>
            <SaveButton />
            <CancelButton />
          </>
        )}
      </div>
      <PdfExportModeModal
        openModal={isPdfModeModalOpen}
        setOpenModal={setIsPdfModeModalOpen}
        defaultMode={defaultPdfMode}
        showMaximumRecommendation={showMaximumRecommendation}
        isLoading={loading}
        onConfirm={handleConfirmPdfMode}
      />
    </HeaderTitle>
  )
}
